import React from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { getChatHeaderSecondaryText, getFormattedDateTime } from 'features/Chat/utils'
import type { IAvatars, IChatHeader } from 'features/Chat/Chat.types'

/**
 * Up to two overlapping 48px avatars, then a "+N" chip. The shared
 * renderChatHeaderAvatars hard-codes the 40px default size, which is too small
 * for a 72px iOS-style row.
 */
const AvatarStack = ({ avatars = [] as IAvatars }) => {
	const shown = avatars.slice(0, 2)
	const overflow = avatars.length - 1

	return (
		<span className="flex shrink-0 -space-x-3">
			{shown.map((avatar, i) =>
				i === 1 && avatars.length > 2 ? (
					<Avatar key={i} className="h-12 w-12 ring-2 ring-white">
						<AvatarFallback className="bg-[#0080fe] text-[12px] font-semibold text-white">
							{`+${overflow}`}
						</AvatarFallback>
					</Avatar>
				) : (
					<Avatar key={i} className="h-12 w-12 ring-2 ring-white">
						<AvatarImage src={avatar.src} alt={avatar.name} />
						<AvatarFallback className="text-[14px] font-medium text-gray-600">
							{avatar.name?.charAt(0).toUpperCase() || '?'}
						</AvatarFallback>
					</Avatar>
				),
			)}
			{shown.length === 0 && (
				<Avatar className="h-12 w-12">
					<AvatarFallback>?</AvatarFallback>
				</Avatar>
			)}
		</span>
	)
}

export interface Props {
	chatHeader: IChatHeader
	userId: string
	isUnread: boolean
	onClick: (chatHeader: IChatHeader) => void
}

const ConversationRow = ({ chatHeader, userId, isUnread, onClick }: Props) => {
	const preview = getChatHeaderSecondaryText(chatHeader.message, chatHeader.lastMessage, userId)

	return (
		<li className="list-none">
			<button
				type="button"
				onClick={() => onClick(chatHeader)}
				data-testid="chat-popup-row"
				className="group flex h-[72px] w-full items-center gap-3 px-4 text-left transition-colors active:bg-gray-100 md:hover:bg-gray-50"
			>
				{/* Placeholder keeps the avatar column aligned when read. */}
				<span
					aria-hidden="true"
					className={cn(
						'h-2.5 w-2.5 shrink-0 rounded-full',
						isUnread ? 'bg-[#0080fe]' : 'bg-transparent',
					)}
				/>

				<AvatarStack avatars={chatHeader.avatars || []} />

				<span className="min-w-0 flex-1">
					<span
						className={cn(
							'block truncate text-[16px] text-gray-900',
							isUnread ? 'font-semibold' : 'font-medium',
						)}
					>
						{chatHeader.label || 'Conversation'}
					</span>
					<span
						className={cn(
							'mt-0.5 block text-[14px] leading-[1.25] line-clamp-2',
							'text-gray-500',
						)}
					>
						{preview}
					</span>
				</span>

				<span className="flex shrink-0 flex-col items-end gap-1">
					<span className="text-[13px] text-gray-400">
						{getFormattedDateTime(chatHeader.updatedAt || chatHeader.createdAt)}
					</span>
					<ChevronRight className="h-4 w-4 text-gray-300 opacity-0 transition-opacity md:group-hover:opacity-100" />
				</span>
			</button>
		</li>
	)
}

export default ConversationRow
